import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DatePicker } from "@/components/ui/date-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import {
  Settings, Globe, Volume2, VolumeX, Mail, Send, MessageCircle, Shield, Database,
  RefreshCw, CheckCircle, XCircle, AlertCircle, Key, Eye, EyeOff,
  Save, Loader2, TestTube, Play, Pause, Upload, Download, Trash2,
  Lock, Server, Zap, Users, Plus, Clock, Search, Bell, Cloud, Copy,
  DoorOpen, FileText, HardDrive, Link, LogOut, X
} from "lucide-react";
import BitsyQRCode from "@/components/BitsyQRCode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SettingsTab({
  portalSettings, setPortalSettings, savingSettings, setSavingSettings,
  emailAlertSettings, setEmailAlertSettings, newRecipientEmail, setNewRecipientEmail,
  emailPreview, setEmailPreview, previewLoading, setPreviewLoading,
  telegramSettings, setTelegramSettings, telegramLoading, setTelegramLoading,
  telegramTestingChannel, setTelegramTestingChannel,
  discoveredGroups, setDiscoveredGroups, discoverGroupsLoading, setDiscoverGroupsLoading,
  showGroupSelector, setShowGroupSelector, selectedChannelForGroup, setSelectedChannelForGroup,
  syncStatus, setSyncStatus, runningSyncTest, setRunningSyncTest,
  syncTargetDate, setSyncTargetDate, syncIncludePrevDay, setSyncIncludePrevDay,
  markingNoBill, setMarkingNoBill,
  testingEmail, setTestingEmail, testingZoho, setTestingZoho,
  uploadingZoho, setUploadingZoho, zohoUploadDate, setZohoUploadDate,
  backingUpDatabase, setBackingUpDatabase, lastBackupResult, setLastBackupResult,
  showPasswordChange, setShowPasswordChange, passwordForm, setPasswordForm,
  changingPassword, setChangingPassword, otpSent, setOtpSent, sendingOtp, setSendingOtp,
  fetchSettings, handleSaveSettings, handleTestConnection, handleRunSync, handleResetSync,
  handleMarkNoBill, saveTelegramSettings, testTelegramChannel, updateTelegramChannel,
  discoverTelegramGroups, selectGroupForChannel,
  fetchEmailAlertSettings, updateEmailAlertSettings, addEmailRecipient, removeEmailRecipient,
  toggleRecipientAlert, sendTestAlert, previewEmailAlert,
  getRecipientAlerts, getRecipientEmail,
  handleRequestPasswordChangeOTP, handleChangePassword,
  rooms
}) {
  return (
    <>
              {/* Header */}
              <div className="mb-6">
                <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Portal Settings</h1>
                <p className="text-vault-text-secondary font-manrope mt-1">Configure API Global portal integration for automated verification</p>
              </div>

              {/* Settings Card */}
              <Card className="bg-vault-surface border-vault-border max-w-2xl" data-testid="settings-card">
                <CardHeader className="border-b border-vault-border">
                  <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2">
                    <Globe className="w-5 h-5 text-vault-gold" />
                    API Global Portal Credentials
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Username */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block font-medium">
                      Portal Username
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" />
                      <Input
                        value={portalSettings.api_global_username}
                        onChange={(e) => setPortalSettings({...portalSettings, api_global_username: e.target.value})}
                        placeholder="Enter your API Global username"
                        className="bg-black/50 border-vault-border text-vault-text pl-10"
                        data-testid="portal-username-input"
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block font-medium">
                      Portal Password
                      {portalSettings.api_global_password_set && (
                        <span className="text-green-400 ml-2 normal-case">(Password saved)</span>
                      )}
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" />
                      <Input
                        type="password"
                        value={portalSettings.api_global_password}
                        onChange={(e) => setPortalSettings({...portalSettings, api_global_password: e.target.value})}
                        placeholder={portalSettings.api_global_password_set ? "Enter new password to change" : "Enter your API Global password"}
                        className="bg-black/50 border-vault-border text-vault-text pl-10"
                        data-testid="portal-password-input"
                      />
                    </div>
                    <p className="text-vault-text-secondary text-xs mt-1">
                      Password is encrypted and stored securely. Leave blank to keep existing password.
                    </p>
                  </div>

                  {/* Alert Email */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block font-medium">
                      Alert Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" />
                      <Input
                        type="email"
                        value={portalSettings.alert_email}
                        onChange={(e) => setPortalSettings({...portalSettings, alert_email: e.target.value})}
                        placeholder="Enter email for sync alerts"
                        className="bg-black/50 border-vault-border text-vault-text pl-10"
                        data-testid="alert-email-input"
                      />
                    </div>
                    <p className="text-vault-text-secondary text-xs mt-1">
                      Receive notifications for missing records and sync status.
                    </p>
                  </div>

                  {/* Telegram Chat ID */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block font-medium">
                      Telegram Chat ID (Group)
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" />
                        <Input
                          type="text"
                          value={portalSettings.telegram_chat_id}
                          onChange={(e) => setPortalSettings({...portalSettings, telegram_chat_id: e.target.value})}
                          placeholder="e.g. -1001234567890"
                          className="bg-black/50 border-vault-border text-vault-text pl-10"
                          data-testid="telegram-chat-id-input"
                        />
                      </div>
                      <Button
                        onClick={async () => {
                          try {
                            // First save the settings
                            await axios.post(`${API}/admin/settings`, { 
                              telegram_chat_id: portalSettings.telegram_chat_id 
                            });
                            // Then test
                            const response = await axios.post(`${API}/admin/settings/test-telegram`);
                            if (response.data.success) {
                              toast.success(`✅ ${response.data.message}`);
                            } else {
                              toast.error(`❌ ${response.data.message}`);
                            }
                          } catch (error) {
                            toast.error("Failed to test Telegram");
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                        data-testid="test-telegram-btn"
                      >
                        Test Telegram
                      </Button>
                    </div>
                    <p className="text-vault-text-secondary text-xs mt-1">
                      All check-in/check-out alerts will be sent to this Telegram group.
                      <br />
                      <span className="text-vault-gold">Correct group ID: -1003798795772</span>
                    </p>
                  </div>

                  {/* Public API Section */}
                  <div className="pt-4 border-t border-vault-border">
                    <h4 className="text-vault-gold font-medium mb-4 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Public API Access
                    </h4>
                    
                    {/* API Key */}
                    <div className="mb-4">
                      <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block font-medium">
                        API Key
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" />
                          <Input
                            type="text"
                            value={portalSettings.public_api_key}
                            onChange={(e) => setPortalSettings({...portalSettings, public_api_key: e.target.value})}
                            placeholder="Enter API key for external access"
                            className="bg-black/50 border-vault-border text-vault-text pl-10 font-mono text-sm"
                            data-testid="public-api-key-input"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={() => {
                            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                            let key = 'hinn_';
                            for (let i = 0; i < 32; i++) {
                              key += chars.charAt(Math.floor(Math.random() * chars.length));
                            }
                            setPortalSettings({...portalSettings, public_api_key: key});
                            toast.success("New secure API key generated! Click Save Settings to apply.");
                          }}
                          className="bg-vault-gold/20 text-vault-gold hover:bg-vault-gold/30 border border-vault-gold/50"
                          data-testid="generate-api-key-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Generate
                        </Button>
                      </div>
                      <p className="text-vault-text-secondary text-xs mt-1">
                        Share this key with authorized systems. Click Generate for a secure random key.
                      </p>
                    </div>

                    {/* Nightly Rate */}
                    <div className="mb-4">
                      <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block font-medium">
                        Railroad Nightly Rate ($)
                      </label>
                      <Input
                        type="number"
                        value={portalSettings.nightly_rate}
                        onChange={(e) => setPortalSettings({...portalSettings, nightly_rate: parseFloat(e.target.value) || 0})}
                        placeholder="75.00"
                        className="bg-black/50 border-vault-border text-vault-text w-32"
                        data-testid="nightly-rate-input"
                      />
                      <p className="text-vault-text-secondary text-xs mt-1">
                        Rate per night used for railroad crew billing.
                      </p>
                    </div>

                    {/* Chatbot & Pricing Settings */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-amber-500/10 to-amber-600/5 rounded-xl border border-amber-500/20">
                      <h4 className="text-vault-gold font-semibold text-sm mb-4 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        Bitsy AI Chatbot Settings
                      </h4>
                      
                      {/* Bitsy Enable/Disable Toggle */}
                      <div className="flex items-center justify-between p-3 bg-black/50 rounded-lg mb-4 border border-vault-border">
                        <div>
                          <p className="text-vault-text font-medium">Bitsy AI Assistant</p>
                          <p className="text-vault-text-secondary text-xs">
                            {portalSettings.bitsy_enabled 
                              ? "Bitsy is accepting online bookings" 
                              : "Bitsy is disabled - no online bookings"}
                          </p>
                        </div>
                        <Button
                          onClick={() => setPortalSettings({...portalSettings, bitsy_enabled: !portalSettings.bitsy_enabled})}
                          className={`${portalSettings.bitsy_enabled 
                            ? 'bg-emerald-600 hover:bg-emerald-700' 
                            : 'bg-red-600 hover:bg-red-700'} text-white min-w-[80px]`}
                          data-testid="bitsy-toggle-btn"
                        >
                          {portalSettings.bitsy_enabled ? 'ON' : 'OFF'}
                        </Button>
                      </div>
                      
                      {/* Bitsy Crew Check-in Toggle - Separate from online bookings */}
                      <div className="flex items-center justify-between p-3 bg-black/50 rounded-lg mb-4 border border-vault-border">
                        <div>
                          <p className="text-vault-text font-medium">Bitsy for Railroad Crew</p>
                          <p className="text-vault-text-secondary text-xs">
                            {portalSettings.bitsy_crew_enabled 
                              ? "Crew can use 'Talk to Bitsy' for check-in/out" 
                              : "Crew will only see quick form (no Bitsy option)"}
                          </p>
                        </div>
                        <Button
                          onClick={() => setPortalSettings({...portalSettings, bitsy_crew_enabled: !portalSettings.bitsy_crew_enabled})}
                          className={`${portalSettings.bitsy_crew_enabled 
                            ? 'bg-emerald-600 hover:bg-emerald-700' 
                            : 'bg-red-600 hover:bg-red-700'} text-white min-w-[80px]`}
                          data-testid="bitsy-crew-toggle-btn"
                        >
                          {portalSettings.bitsy_crew_enabled ? 'ON' : 'OFF'}
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        {/* Railroad Room Rate */}
                        <div>
                          <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">
                            Railroad Rate ($/night)
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            value={portalSettings.railroad_room_rate}
                            onChange={(e) => setPortalSettings({...portalSettings, railroad_room_rate: parseFloat(e.target.value) || 0})}
                            placeholder="56.00"
                            className="bg-black/50 border-vault-border text-vault-text"
                            data-testid="railroad-room-rate-input"
                          />
                        </div>
                        
                        {/* API Commission Rate */}
                        <div>
                          <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">
                            API Commission (%)
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            value={portalSettings.api_commission_rate}
                            onChange={(e) => setPortalSettings({...portalSettings, api_commission_rate: parseFloat(e.target.value) || 0})}
                            placeholder="10"
                            className="bg-black/50 border-vault-border text-vault-text"
                            data-testid="api-commission-rate-input"
                          />
                        </div>
                        
                        {/* Single Room Rate */}
                        <div>
                          <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">
                            Single Bed Rate ($)
                          </label>
                          <Input
                            type="number"
                            value={portalSettings.single_room_rate}
                            onChange={(e) => setPortalSettings({...portalSettings, single_room_rate: parseFloat(e.target.value) || 0})}
                            placeholder="85.00"
                            className="bg-black/50 border-vault-border text-vault-text"
                            data-testid="single-room-rate-input"
                          />
                        </div>
                        
                        {/* Double Room Rate */}
                        <div>
                          <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">
                            Double Bed Rate ($)
                          </label>
                          <Input
                            type="number"
                            value={portalSettings.double_room_rate}
                            onChange={(e) => setPortalSettings({...portalSettings, double_room_rate: parseFloat(e.target.value) || 0})}
                            placeholder="95.00"
                            className="bg-black/50 border-vault-border text-vault-text"
                            data-testid="double-room-rate-input"
                          />
                        </div>
                        
                        {/* Sales Tax */}
                        <div>
                          <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">
                            Sales Tax (%)
                          </label>
                          <Input
                            type="number"
                            step="0.1"
                            value={portalSettings.sales_tax_rate}
                            onChange={(e) => setPortalSettings({...portalSettings, sales_tax_rate: parseFloat(e.target.value) || 0})}
                            placeholder="10.3"
                            className="bg-black/50 border-vault-border text-vault-text"
                            data-testid="sales-tax-rate-input"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Max Chatbot Rooms */}
                        <div>
                          <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">
                            Max Chatbot Rooms/Day
                          </label>
                          <Input
                            type="number"
                            value={portalSettings.chatbot_max_rooms}
                            onChange={(e) => setPortalSettings({...portalSettings, chatbot_max_rooms: parseInt(e.target.value) || 0})}
                            placeholder="3"
                            className="bg-black/50 border-vault-border text-vault-text"
                            data-testid="chatbot-max-rooms-input"
                          />
                          <p className="text-vault-text-secondary text-xs mt-1">
                            Limit online bookings to protect railroad guarantee
                          </p>
                        </div>
                        
                        {/* Guaranteed Rooms */}
                        <div>
                          <label className="text-xs text-vault-text-secondary uppercase tracking-wider mb-1 block">
                            Guaranteed Railroad Rooms
                          </label>
                          <Input
                            type="number"
                            value={portalSettings.guaranteed_rooms}
                            onChange={(e) => setPortalSettings({...portalSettings, guaranteed_rooms: parseInt(e.target.value) || 0})}
                            placeholder="25"
                            className="bg-black/50 border-vault-border text-vault-text"
                            data-testid="guaranteed-rooms-input"
                          />
                          <p className="text-vault-text-secondary text-xs mt-1">
                            Rooms reserved for CPKC crew
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-black/30 rounded-lg">
                        <p className="text-vault-text-secondary text-xs">
                          <span className="text-vault-gold font-medium">Chatbot URL:</span>{" "}
                          <a 
                            href="/book" 
                            target="_blank" 
                            className="text-blue-400 hover:underline"
                          >
                            {window.location.origin}/book
                          </a>
                        </p>
                        <p className="text-vault-text-secondary text-xs mt-1">
                          The AI chatbot checks real-time availability and won't oversell beyond the limit above.
                        </p>
                      </div>
                    </div>

                    {/* API Documentation Panel */}
                    {portalSettings.public_api_key && (
                      <div className="bg-black/70 rounded-lg p-4 mt-4 border border-vault-border">
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-vault-gold text-sm font-medium flex items-center gap-2">
                            <Link className="w-4 h-4" />
                            API Documentation
                          </p>
                          <Button
                            size="sm"
                            onClick={() => {
                              const baseUrl = window.location.origin;
                              const docText = `HODLER INN API DOCUMENTATION

BASE URL: ${baseUrl}

API KEY: ${portalSettings.public_api_key}

ENDPOINTS:

1. Sign-in Sheets
   ${baseUrl}/api/public/signin-sheets?api_key=${portalSettings.public_api_key}

2. Billing Report
   ${baseUrl}/api/public/billing-report?api_key=${portalSettings.public_api_key}

OPTIONAL PARAMETERS:
- format=csv (default: json)
- start_date=YYYY-MM-DD
- end_date=YYYY-MM-DD

EXAMPLES:

All records (JSON):
${baseUrl}/api/public/signin-sheets?api_key=${portalSettings.public_api_key}

Billing as CSV:
${baseUrl}/api/public/billing-report?api_key=${portalSettings.public_api_key}&format=csv

Date filtered:
${baseUrl}/api/public/signin-sheets?api_key=${portalSettings.public_api_key}&start_date=2026-03-01&end_date=2026-03-31`;
                              navigator.clipboard.writeText(docText);
                              toast.success("Full API documentation copied to clipboard!");
                            }}
                            className="bg-vault-gold/20 text-vault-gold hover:bg-vault-gold/30 text-xs"
                            data-testid="copy-all-docs-btn"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy All
                          </Button>
                        </div>

                        {/* Base URL */}
                        <div className="mb-3">
                          <label className="text-vault-text-secondary text-xs mb-1 block">Base URL</label>
                          <div className="flex items-center gap-2 bg-black/50 rounded px-3 py-2">
                            <code className="text-green-400 text-sm flex-1 break-all">{window.location.origin}</code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                navigator.clipboard.writeText(window.location.origin);
                                toast.success("Base URL copied!");
                              }}
                              className="h-6 w-6 p-0 text-vault-text-secondary hover:text-vault-gold"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* API Key Display */}
                        <div className="mb-3">
                          <label className="text-vault-text-secondary text-xs mb-1 block">Your API Key</label>
                          <div className="flex items-center gap-2 bg-black/50 rounded px-3 py-2">
                            <code className="text-amber-400 text-sm flex-1 font-mono">{portalSettings.public_api_key}</code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                navigator.clipboard.writeText(portalSettings.public_api_key);
                                toast.success("API Key copied!");
                              }}
                              className="h-6 w-6 p-0 text-vault-text-secondary hover:text-vault-gold"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Endpoints */}
                        <div className="space-y-3">
                          <label className="text-vault-text-secondary text-xs block">Endpoints (Click to copy full URL)</label>
                          
                          {/* Sign-in Sheets Endpoint */}
                          <div 
                            className="bg-black/50 rounded px-3 py-2 cursor-pointer hover:bg-black/70 transition-colors group"
                            onClick={() => {
                              const url = `${window.location.origin}/api/public/signin-sheets?api_key=${portalSettings.public_api_key}`;
                              navigator.clipboard.writeText(url);
                              toast.success("Sign-in Sheets URL copied!");
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded mr-2">GET</span>
                                <span className="text-vault-text text-sm">Sign-in Sheets</span>
                              </div>
                              <Copy className="w-3 h-3 text-vault-text-secondary group-hover:text-vault-gold" />
                            </div>
                            <code className="text-vault-text-secondary text-xs mt-1 block break-all">
                              /api/public/signin-sheets?api_key=...
                            </code>
                          </div>

                          {/* Billing Report Endpoint */}
                          <div 
                            className="bg-black/50 rounded px-3 py-2 cursor-pointer hover:bg-black/70 transition-colors group"
                            onClick={() => {
                              const url = `${window.location.origin}/api/public/billing-report?api_key=${portalSettings.public_api_key}`;
                              navigator.clipboard.writeText(url);
                              toast.success("Billing Report URL copied!");
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded mr-2">GET</span>
                                <span className="text-vault-text text-sm">Billing Report</span>
                              </div>
                              <Copy className="w-3 h-3 text-vault-text-secondary group-hover:text-vault-gold" />
                            </div>
                            <code className="text-vault-text-secondary text-xs mt-1 block break-all">
                              /api/public/billing-report?api_key=...
                            </code>
                          </div>
                        </div>

                        {/* Parameters Table */}
                        <div className="mt-4 pt-3 border-t border-vault-border">
                          <label className="text-vault-text-secondary text-xs mb-2 block">Optional Parameters</label>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="bg-black/50 rounded p-2">
                              <code className="text-blue-400">format</code>
                              <p className="text-vault-text-secondary mt-1">json / csv</p>
                            </div>
                            <div className="bg-black/50 rounded p-2">
                              <code className="text-blue-400">start_date</code>
                              <p className="text-vault-text-secondary mt-1">YYYY-MM-DD</p>
                            </div>
                            <div className="bg-black/50 rounded p-2">
                              <code className="text-blue-400">end_date</code>
                              <p className="text-vault-text-secondary mt-1">YYYY-MM-DD</p>
                            </div>
                          </div>
                        </div>

                        {/* Quick Links */}
                        <div className="mt-4 pt-3 border-t border-vault-border">
                          <label className="text-vault-text-secondary text-xs mb-2 block">Quick Test Links</label>
                          <div className="flex flex-wrap gap-2">
                            <a
                              href={`${window.location.origin}/api/public/signin-sheets?api_key=${portalSettings.public_api_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs bg-vault-gold/20 text-vault-gold px-3 py-1.5 rounded hover:bg-vault-gold/30 transition-colors"
                            >
                              Open Sign-in (JSON)
                            </a>
                            <a
                              href={`${window.location.origin}/api/public/signin-sheets?api_key=${portalSettings.public_api_key}&format=csv`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs bg-vault-gold/20 text-vault-gold px-3 py-1.5 rounded hover:bg-vault-gold/30 transition-colors"
                            >
                              Download Sign-in (CSV)
                            </a>
                            <a
                              href={`${window.location.origin}/api/public/billing-report?api_key=${portalSettings.public_api_key}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs bg-vault-gold/20 text-vault-gold px-3 py-1.5 rounded hover:bg-vault-gold/30 transition-colors"
                            >
                              Open Billing (JSON)
                            </a>
                            <a
                              href={`${window.location.origin}/api/public/billing-report?api_key=${portalSettings.public_api_key}&format=csv`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs bg-vault-gold/20 text-vault-gold px-3 py-1.5 rounded hover:bg-vault-gold/30 transition-colors"
                            >
                              Download Billing (CSV)
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Save Button */}
                  <div className="pt-4 border-t border-vault-border">
                    <Button
                      onClick={handleSaveSettings}
                      disabled={savingSettings}
                      className="vault-btn-primary flex items-center gap-2"
                      data-testid="save-settings-btn"
                    >
                      <Save className="w-4 h-4" />
                      {savingSettings ? "Saving..." : "Save Settings"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Sync Controls Card */}
              <Card className="bg-vault-surface border-vault-border max-w-2xl mt-6" data-testid="sync-controls-card">
                <CardHeader className="border-b border-vault-border">
                  <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2">
                    <Globe className="w-5 h-5 text-vault-gold" />
                    API Global Sync
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Manual Sync Buttons */}
                  <div className="space-y-3">
                    {/* Date Picker for Manual Sync */}
                    <div className="flex items-center gap-3 bg-black/30 p-3 rounded-lg border border-vault-border">
                      <label className="text-vault-text-secondary text-sm whitespace-nowrap">Sync Date:</label>
                      <DatePicker
                        date={syncTargetDate}
                        onDateChange={setSyncTargetDate}
                        placeholder="Select sync date"
                      />
                      <span className="text-vault-text-secondary text-xs">
                        {syncTargetDate ? `Selected: ${syncTargetDate}` : "Leave empty for yesterday/today"}
                      </span>
                      <label className="flex items-center gap-2 text-vault-text-secondary text-sm cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={syncIncludePrevDay}
                          onChange={(e) => setSyncIncludePrevDay(e.target.checked)}
                          className="w-4 h-4 accent-vault-gold"
                        />
                        Include previous day (for late arrivals)
                      </label>
                    </div>
                    
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={handleTestConnection}
                        disabled={runningSyncTest || !portalSettings.api_global_password_set}
                        className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                        data-testid="test-connection-btn"
                      >
                        <Key className="w-4 h-4" />
                        {runningSyncTest ? "Testing..." : "Test Connection"}
                      </Button>
                      <Button
                        onClick={handleRunSync}
                        disabled={syncStatus.running || !portalSettings.api_global_password_set}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                        data-testid="run-sync-btn"
                      >
                        <Globe className="w-4 h-4" />
                        {syncStatus.running ? "Syncing..." : "Run Sync Now"}
                      </Button>
                      <Button
                        onClick={handleResetSync}
                        variant="outline"
                        className="border-vault-border text-vault-text-secondary hover:bg-vault-border/30 flex items-center gap-2"
                        data-testid="reset-sync-btn"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Reset
                      </Button>
                    </div>
                  </div>
                  
                  {!portalSettings.api_global_password_set && (
                    <p className="text-amber-400 text-sm">
                      Please save your portal credentials first before running sync.
                    </p>
                  )}
                  
                  {syncStatus.running && (
                    <div className="bg-black/50 border border-vault-border rounded-lg p-4">
                      <p className="text-vault-gold text-sm flex items-center gap-2">
                        <span className="animate-spin">⚡</span>
                        {syncStatus.progress || "Sync in progress..."}
                      </p>
                    </div>
                  )}
                  
                  {syncStatus.last_results && !syncStatus.running && (
                    <div className="bg-black/50 border border-vault-border rounded-lg p-4">
                      <h4 className="text-vault-gold font-bold mb-2">Last Sync Results:</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <p className="text-green-400">Verified: {syncStatus.last_results.verified?.length || 0}</p>
                        <p className="text-amber-400">No Bill: {syncStatus.last_results.no_bill?.length || 0}</p>
                        <p className="text-blue-400">Missing: {syncStatus.last_results.missing_in_hodler?.length || 0}</p>
                        <p className="text-red-400">Errors: {syncStatus.last_results.errors?.length || 0}</p>
                      </div>
                      {syncStatus.last_run && (
                        <p className="text-vault-text-secondary text-xs mt-2">
                          Last run: {new Date(syncStatus.last_run).toLocaleString()}
                        </p>
                      )}
                      <button
                        onClick={() => {
                          window.open(withAdminAuthToken(`${API}/admin/sync/export-pdf`), '_blank');
                        }}
                        className="mt-3 w-full px-4 py-2 bg-vault-gold/20 hover:bg-vault-gold/30 text-vault-gold border border-vault-gold/50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        data-testid="download-sync-report-btn"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download PDF Report
                      </button>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => {
                            window.open(withAdminAuthToken(`${API}/admin/sync/view-pdf`), '_blank');
                          }}
                          className="flex-1 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                          data-testid="view-sync-report-btn"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Report
                        </button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Voice Control */}
              <Card className="bg-vault-surface border-vault-border max-w-2xl mt-6" data-testid="voice-control-card">
                <CardHeader className="border-b border-vault-border">
                  <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2">
                    {portalSettings.voice_enabled ? (
                      <Volume2 className="w-5 h-5 text-vault-gold" />
                    ) : (
                      <VolumeX className="w-5 h-5 text-vault-text-secondary" />
                    )}
                    Voice Messages
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <p className="text-vault-text-secondary text-sm">
                    Control voice announcements on the guest kiosk after check-in and check-out.
                  </p>
                  
                  {/* Voice Enable/Disable Toggle */}
                  <div className="bg-black/50 border border-vault-border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-vault-text font-medium">Voice Announcements</h4>
                        <p className="text-vault-text-secondary text-sm">
                          {portalSettings.voice_enabled 
                            ? "Voice messages will play after check-in and check-out" 
                            : "Voice messages are muted"}
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !portalSettings.voice_enabled;
                          setPortalSettings({...portalSettings, voice_enabled: newValue});
                          try {
                            await axios.post(`${API}/admin/settings`, { voice_enabled: newValue });
                            toast.success(newValue ? "Voice enabled" : "Voice muted");
                          } catch (error) {
                            toast.error("Failed to update voice setting");
                            setPortalSettings({...portalSettings, voice_enabled: !newValue});
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          portalSettings.voice_enabled ? 'bg-emerald-600' : 'bg-gray-600'
                        } cursor-pointer`}
                        data-testid="voice-toggle"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            portalSettings.voice_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  
                  {/* Volume Slider */}
                  {portalSettings.voice_enabled && (
                    <div className="bg-black/50 border border-vault-border rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-vault-text font-medium">Volume</h4>
                          <span className="text-vault-gold font-mono text-sm">
                            {Math.round(portalSettings.voice_volume * 100)}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={portalSettings.voice_volume * 100}
                          onChange={async (e) => {
                            const newVolume = parseInt(e.target.value) / 100;
                            setPortalSettings({...portalSettings, voice_volume: newVolume});
                          }}
                          onMouseUp={async (e) => {
                            const newVolume = parseInt(e.target.value) / 100;
                            try {
                              await axios.post(`${API}/admin/settings`, { voice_volume: newVolume });
                              toast.success(`Volume set to ${Math.round(newVolume * 100)}%`);
                            } catch (error) {
                              toast.error("Failed to update volume");
                            }
                          }}
                          onTouchEnd={async (e) => {
                            const newVolume = portalSettings.voice_volume;
                            try {
                              await axios.post(`${API}/admin/settings`, { voice_volume: newVolume });
                              toast.success(`Volume set to ${Math.round(newVolume * 100)}%`);
                            } catch (error) {
                              toast.error("Failed to update volume");
                            }
                          }}
                          className="w-full h-2 bg-vault-border rounded-lg appearance-none cursor-pointer accent-vault-gold"
                          data-testid="voice-volume-slider"
                        />
                        <div className="flex justify-between text-vault-text-secondary text-xs">
                          <span>Mute</span>
                          <span>Max</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Voice Speed Control */}
                  {portalSettings.voice_enabled && (
                    <div className="bg-black/50 border border-vault-border rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-vault-text font-medium">Voice Speed</h4>
                          <span className="text-vault-gold font-mono text-sm">
                            {portalSettings.voice_speed <= 0.7 ? 'Slow' : portalSettings.voice_speed >= 1.0 ? 'Fast' : 'Normal'} ({portalSettings.voice_speed})
                          </span>
                        </div>
                        <input
                          type="range"
                          min="50"
                          max="120"
                          step="5"
                          value={portalSettings.voice_speed * 100}
                          onChange={async (e) => {
                            const newSpeed = parseInt(e.target.value) / 100;
                            setPortalSettings({...portalSettings, voice_speed: newSpeed});
                          }}
                          onMouseUp={async (e) => {
                            const newSpeed = parseInt(e.target.value) / 100;
                            try {
                              await axios.post(`${API}/admin/settings`, { voice_speed: newSpeed });
                              toast.success(`Voice speed set to ${newSpeed}`);
                            } catch (error) {
                              toast.error("Failed to update voice speed");
                            }
                          }}
                          onTouchEnd={async (e) => {
                            const newSpeed = portalSettings.voice_speed;
                            try {
                              await axios.post(`${API}/admin/settings`, { voice_speed: newSpeed });
                              toast.success(`Voice speed set to ${newSpeed}`);
                            } catch (error) {
                              toast.error("Failed to update voice speed");
                            }
                          }}
                          className="w-full h-2 bg-vault-border rounded-lg appearance-none cursor-pointer accent-vault-gold"
                          data-testid="voice-speed-slider"
                        />
                        <div className="flex justify-between text-vault-text-secondary text-xs">
                          <span>Slow (0.5)</span>
                          <span>Normal (0.85)</span>
                          <span>Fast (1.2)</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Test Voice Button */}
                  <Button
                    onClick={() => {
                      if ('speechSynthesis' in window) {
                        window.speechSynthesis.cancel();
                        const utterance = new SpeechSynthesisUtterance("Good afternoon! Welcome to Hodler Inn. Please enter room number, time, sign your name, and click Complete Check-In.");
                        utterance.volume = portalSettings.voice_volume;
                        utterance.rate = portalSettings.voice_speed;
                        window.speechSynthesis.speak(utterance);
                        toast.success("Playing test message...");
                      } else {
                        toast.error("Voice not supported in this browser");
                      }
                    }}
                    disabled={!portalSettings.voice_enabled}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                    data-testid="test-voice-btn"
                  >
                    <Volume2 className="w-4 h-4" />
                    Test Voice
                  </Button>
                </CardContent>
              </Card>

              {/* Email Reports Section */}
              <Card className="bg-vault-surface-highlight/50 border-vault-border max-w-2xl mt-6">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-outfit text-lg font-bold text-vault-gold flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Daily Email Reports
                    </h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-vault-text-secondary text-sm">
                        {portalSettings.email_reports_enabled ? "Enabled" : "Disabled"}
                      </span>
                      <input
                        type="checkbox"
                        checked={portalSettings.email_reports_enabled}
                        onChange={async (e) => {
                          const enabled = e.target.checked;
                          setPortalSettings({...portalSettings, email_reports_enabled: enabled});
                          try {
                            await axios.post(`${API}/admin/settings`, { email_reports_enabled: enabled });
                            toast.success(enabled ? "Email reports enabled" : "Email reports disabled");
                          } catch (error) {
                            toast.error("Failed to update setting");
                          }
                        }}
                        className="w-5 h-5 accent-vault-gold"
                        data-testid="email-reports-toggle"
                      />
                    </label>
                  </div>
                  <p className="text-vault-text-secondary text-sm">
                    Automatically email sign-in sheets and billing records as PDF attachments daily.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* SMTP Host */}
                    <div>
                      <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">SMTP Host</label>
                      <Input
                        value={portalSettings.email_smtp_host}
                        onChange={(e) => setPortalSettings({...portalSettings, email_smtp_host: e.target.value})}
                        placeholder="smtp.zoho.com"
                        className="bg-black/50 border-vault-border text-vault-text"
                        data-testid="email-smtp-host"
                      />
                    </div>

                    {/* SMTP Port */}
                    <div>
                      <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">SMTP Port</label>
                      <Input
                        type="number"
                        value={portalSettings.email_smtp_port}
                        onChange={(e) => setPortalSettings({...portalSettings, email_smtp_port: parseInt(e.target.value) || 587})}
                        placeholder="587"
                        className="bg-black/50 border-vault-border text-vault-text"
                        data-testid="email-smtp-port"
                      />
                    </div>
                  </div>

                  {/* Sender Email */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Sender Email (Your Zoho Email)</label>
                    <Input
                      value={portalSettings.email_sender}
                      onChange={(e) => setPortalSettings({...portalSettings, email_sender: e.target.value})}
                      placeholder="your-email@yourdomain.com"
                      className="bg-black/50 border-vault-border text-vault-text"
                      data-testid="email-sender"
                    />
                  </div>

                  {/* Email Password */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">
                      Email Password {portalSettings.email_password_set && <span className="text-emerald-400">(Set ✓)</span>}
                    </label>
                    <Input
                      type="password"
                      value={portalSettings.email_password}
                      onChange={(e) => setPortalSettings({...portalSettings, email_password: e.target.value})}
                      placeholder={portalSettings.email_password_set ? "••••••••" : "Enter password or app password"}
                      className="bg-black/50 border-vault-border text-vault-text"
                      data-testid="email-password"
                    />
                    <p className="text-vault-text-secondary text-xs mt-1">
                      Use an App Password if you have 2FA enabled on Zoho
                    </p>
                  </div>

                  {/* Recipient Email */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Send Reports To</label>
                    <Input
                      value={portalSettings.email_recipient}
                      onChange={(e) => setPortalSettings({...portalSettings, email_recipient: e.target.value})}
                      placeholder="signinsheets@hodlerinn.com"
                      className="bg-black/50 border-vault-border text-vault-text"
                      data-testid="email-recipient"
                    />
                  </div>

                  {/* Report Time */}
                  <div>
                    <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Send Report At (Your Local Time)</label>
                    <Input
                      type="time"
                      value={portalSettings.email_report_time}
                      onChange={(e) => setPortalSettings({...portalSettings, email_report_time: e.target.value})}
                      className="bg-black/50 border-vault-border text-vault-text w-40"
                      data-testid="email-report-time"
                    />
                  </div>

                  {/* Save & Test Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={async () => {
                        setSavingSettings(true);
                        try {
                          await axios.post(`${API}/admin/settings`, {
                            email_reports_enabled: portalSettings.email_reports_enabled,
                            email_smtp_host: portalSettings.email_smtp_host,
                            email_smtp_port: portalSettings.email_smtp_port,
                            email_sender: portalSettings.email_sender,
                            email_password: portalSettings.email_password || null,
                            email_recipient: portalSettings.email_recipient,
                            email_report_time: portalSettings.email_report_time
                          });
                          toast.success("Email settings saved!");
                          fetchSettings();
                        } catch (error) {
                          toast.error("Failed to save settings");
                        } finally {
                          setSavingSettings(false);
                        }
                      }}
                      disabled={savingSettings}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="save-email-settings-btn"
                    >
                      {savingSettings ? "Saving..." : "Save Email Settings"}
                    </Button>
                    <Button
                      onClick={async () => {
                        setTestingEmail(true);
                        try {
                          const res = await axios.post(`${API}/admin/settings/test-email`);
                          if (res.data.success) {
                            toast.success(res.data.message);
                          } else {
                            toast.error(res.data.message);
                          }
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to send test email");
                        } finally {
                          setTestingEmail(false);
                        }
                      }}
                      disabled={testingEmail || !portalSettings.email_sender}
                      className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                      data-testid="test-email-btn"
                    >
                      <Mail className="w-4 h-4" />
                      {testingEmail ? "Sending..." : "Send Test Email"}
                    </Button>
                  </div>
                  
                  {/* CPKC Email Notification Test Buttons */}
                  <div className="border-t border-vault-border pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-vault-gold mb-3">Email Alert Recipients</h4>
                    <p className="text-vault-text-secondary text-xs mb-3">
                      Manage email recipients and customize which alerts each person receives
                    </p>
                    
                    {/* Current Recipients with Per-Recipient Alert Settings */}
                    <div className="bg-black/30 rounded-lg p-3 mb-3">
                      <div className="grid grid-cols-[1fr_repeat(4,auto)_auto] gap-2 text-xs text-vault-text-secondary mb-2 px-2">
                        <span>Email</span>
                        <span className="text-center w-16">Sold Out</span>
                        <span className="text-center w-16">Available</span>
                        <span className="text-center w-16">Low Avail</span>
                        <span className="text-center w-16">Daily</span>
                        <span className="w-8"></span>
                      </div>
                      <div className="space-y-2">
                        {emailAlertSettings.recipients?.length > 0 ? (
                          emailAlertSettings.recipients.map((recipient, idx) => {
                            const email = getRecipientEmail(recipient);
                            const alerts = getRecipientAlerts(recipient);
                            return (
                              <div key={idx} className="grid grid-cols-[1fr_repeat(4,auto)_auto] gap-2 items-center bg-vault-surface border border-vault-border rounded-lg px-2 py-2" data-testid={`recipient-row-${idx}`}>
                                <span className="text-vault-text text-sm truncate" title={email}>{email}</span>
                                <div className="flex justify-center w-16">
                                  <input
                                    type="checkbox"
                                    checked={alerts.includes('sold_out')}
                                    onChange={() => toggleRecipientAlert(email, 'sold_out', alerts)}
                                    className="w-4 h-4 accent-vault-gold cursor-pointer"
                                    title="Sold Out alerts"
                                    data-testid={`alert-sold-out-${idx}`}
                                  />
                                </div>
                                <div className="flex justify-center w-16">
                                  <input
                                    type="checkbox"
                                    checked={alerts.includes('rooms_available')}
                                    onChange={() => toggleRecipientAlert(email, 'rooms_available', alerts)}
                                    className="w-4 h-4 accent-vault-gold cursor-pointer"
                                    title="Rooms Available alerts"
                                    data-testid={`alert-rooms-available-${idx}`}
                                  />
                                </div>
                                <div className="flex justify-center w-16">
                                  <input
                                    type="checkbox"
                                    checked={alerts.includes('heads_up')}
                                    onChange={() => toggleRecipientAlert(email, 'heads_up', alerts)}
                                    className="w-4 h-4 accent-vault-gold cursor-pointer"
                                    title="Low Availability alerts"
                                    data-testid={`alert-heads-up-${idx}`}
                                  />
                                </div>
                                <div className="flex justify-center w-16">
                                  <input
                                    type="checkbox"
                                    checked={alerts.includes('daily_status')}
                                    onChange={() => toggleRecipientAlert(email, 'daily_status', alerts)}
                                    className="w-4 h-4 accent-vault-gold cursor-pointer"
                                    title="Daily Status alerts (7AM & 10PM)"
                                    data-testid={`alert-daily-status-${idx}`}
                                  />
                                </div>
                                <button 
                                  onClick={() => removeEmailRecipient(email)}
                                  className="text-red-400 hover:text-red-300 w-8 flex justify-center"
                                  title="Remove recipient"
                                  data-testid={`remove-recipient-${idx}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-vault-text-secondary text-sm px-2">No recipients configured</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Add New Recipient */}
                    <div className="flex gap-2 mb-4">
                      <Input
                        type="email"
                        value={newRecipientEmail}
                        onChange={(e) => setNewRecipientEmail(e.target.value)}
                        placeholder="email@company.com"
                        className="bg-black/50 border-vault-border text-vault-text flex-1"
                        data-testid="new-recipient-email-input"
                      />
                      <Button
                        onClick={addEmailRecipient}
                        className="bg-vault-gold hover:bg-vault-gold/80 text-black"
                        data-testid="add-recipient-btn"
                      >
                        Add
                      </Button>
                    </div>
                    
                    {/* Global Master Toggles */}
                    <div className="bg-black/20 rounded-lg p-3 mb-4">
                      <p className="text-vault-text-secondary text-xs mb-2">Master Alert Switches (disable to stop all alerts of this type):</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={emailAlertSettings.sold_out_enabled}
                            onChange={(e) => {
                              const newSettings = {...emailAlertSettings, sold_out_enabled: e.target.checked};
                              setEmailAlertSettings(newSettings);
                              updateEmailAlertSettings(newSettings);
                            }}
                            className="w-4 h-4 accent-vault-gold"
                            data-testid="master-sold-out-toggle"
                          />
                          <span className="text-vault-text text-sm">Sold Out</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={emailAlertSettings.rooms_available_enabled}
                            onChange={(e) => {
                              const newSettings = {...emailAlertSettings, rooms_available_enabled: e.target.checked};
                              setEmailAlertSettings(newSettings);
                              updateEmailAlertSettings(newSettings);
                            }}
                            className="w-4 h-4 accent-vault-gold"
                            data-testid="master-rooms-available-toggle"
                          />
                          <span className="text-vault-text text-sm">Available</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={emailAlertSettings.heads_up_enabled}
                            onChange={(e) => {
                              const newSettings = {...emailAlertSettings, heads_up_enabled: e.target.checked};
                              setEmailAlertSettings(newSettings);
                              updateEmailAlertSettings(newSettings);
                            }}
                            className="w-4 h-4 accent-vault-gold"
                            data-testid="master-heads-up-toggle"
                          />
                          <span className="text-vault-text text-sm">Low Avail</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={emailAlertSettings.daily_status_enabled !== false}
                            onChange={(e) => {
                              const newSettings = {...emailAlertSettings, daily_status_enabled: e.target.checked};
                              setEmailAlertSettings(newSettings);
                              updateEmailAlertSettings(newSettings);
                            }}
                            className="w-4 h-4 accent-vault-gold"
                            data-testid="master-daily-status-toggle"
                          />
                          <span className="text-vault-text text-sm">Daily Status</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Test Button */}
                    <Button
                      onClick={sendTestAlert}
                      disabled={emailAlertSettings.recipients?.length === 0}
                      className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                      data-testid="send-test-alert-btn"
                    >
                      <Mail className="w-4 h-4" />
                      Send Test Alert to All Recipients
                    </Button>
                  </div>
                  
                  {/* Legacy Test Buttons */}
                  <div className="border-t border-vault-border pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-vault-gold mb-3">Preview & Test Individual Alerts</h4>
                    <p className="text-vault-text-secondary text-xs mb-3">
                      Preview email content or send test notifications
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="space-y-2">
                        <p className="text-vault-text-secondary text-xs">Sold Out</p>
                        <Button
                          onClick={() => previewEmailAlert('sold_out')}
                          variant="outline"
                          className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                          data-testid="preview-sold-out-btn"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <p className="text-vault-text-secondary text-xs">Rooms Available</p>
                        <Button
                          onClick={() => previewEmailAlert('rooms_available')}
                          variant="outline"
                          className="w-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                          data-testid="preview-rooms-available-btn"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <p className="text-vault-text-secondary text-xs">Low Availability</p>
                        <Button
                          onClick={() => previewEmailAlert('heads_up')}
                          variant="outline"
                          className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-xs"
                          data-testid="preview-heads-up-btn"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <p className="text-vault-text-secondary text-xs">Daily Status</p>
                        <Button
                          onClick={() => previewEmailAlert('daily_status')}
                          variant="outline"
                          className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-xs"
                          data-testid="preview-daily-status-btn"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/admin/settings/test-room-available`);
                            if (res.data.success) {
                              toast.success(res.data.message);
                            } else {
                              toast.error(res.data.message);
                            }
                          } catch (error) {
                            toast.error("Failed to send test notification");
                          }
                        }}
                        disabled={!portalSettings.email_sender}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                        data-testid="test-room-available-btn"
                      >
                        Send Room Available Test
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/admin/settings/test-heads-up`);
                            if (res.data.success) {
                              toast.success(res.data.message);
                            } else {
                              toast.error(res.data.message);
                            }
                          } catch (error) {
                            toast.error("Failed to send test notification");
                          }
                        }}
                        disabled={!portalSettings.email_sender}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-sm"
                        data-testid="test-heads-up-btn"
                      >
                        Send Heads-Up Test
                      </Button>
                    </div>
                  </div>
                  
                  {/* Email Preview Modal */}
                  <Dialog open={!!emailPreview} onOpenChange={() => setEmailPreview(null)}>
                    <DialogContent className="bg-vault-surface border-vault-border max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-vault-gold flex items-center gap-2">
                          <Mail className="w-5 h-5" />
                          Email Preview: {emailPreview?.alert_type?.replace('_', ' ').toUpperCase()}
                        </DialogTitle>
                      </DialogHeader>
                      {emailPreview && (
                        <div className="space-y-4">
                          <div className="bg-black/30 rounded-lg p-4">
                            <p className="text-vault-text-secondary text-xs mb-1">SUBJECT:</p>
                            <p className="text-vault-text font-medium">{emailPreview.subject}</p>
                          </div>
                          <div className="bg-black/30 rounded-lg p-4">
                            <p className="text-vault-text-secondary text-xs mb-1">RECIPIENTS ({emailPreview.recipients?.length || 0}):</p>
                            <p className="text-vault-text">{emailPreview.recipients?.join(', ') || 'No recipients configured for this alert'}</p>
                          </div>
                          <div className="bg-black/30 rounded-lg p-4">
                            <p className="text-vault-text-secondary text-xs mb-2">EMAIL BODY:</p>
                            <pre className="text-vault-text text-sm whitespace-pre-wrap font-mono bg-black/20 p-3 rounded">{emailPreview.body}</pre>
                          </div>
                          <p className="text-vault-text-secondary text-xs italic">{emailPreview.preview_note}</p>
                        </div>
                      )}
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" className="border-vault-border text-vault-text">
                            Close
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              {/* Zoho Drive Section */}
              <Card className="bg-vault-surface-highlight/50 border-vault-border max-w-2xl mt-6">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-outfit text-lg font-bold text-vault-gold flex items-center gap-2">
                      <Cloud className="w-5 h-5" />
                      Zoho WorkDrive Backup
                    </h3>
                    <span className="text-emerald-400 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  </div>
                  <p className="text-vault-text-secondary text-sm">
                    Upload sign-in sheets with signatures to your Zoho WorkDrive. Select a date to upload missed reports.
                  </p>
                  
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Report Date</label>
                      <Input
                        type="date"
                        value={zohoUploadDate}
                        onChange={(e) => setZohoUploadDate(e.target.value)}
                        className="bg-vault-surface border-vault-border text-vault-text w-40"
                      />
                    </div>
                    <Button
                      onClick={async () => {
                        setTestingZoho(true);
                        try {
                          const res = await axios.post(`${API}/admin/settings/test-zoho`);
                          if (res.data.success) {
                            toast.success(res.data.message);
                          } else {
                            toast.error(res.data.message);
                          }
                        } catch (error) {
                          toast.error("Failed to test Zoho connection");
                        } finally {
                          setTestingZoho(false);
                        }
                      }}
                      disabled={testingZoho}
                      className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                      data-testid="test-zoho-btn"
                    >
                      <Cloud className="w-4 h-4" />
                      {testingZoho ? "Testing..." : "Test Connection"}
                    </Button>
                    <Button
                      onClick={async () => {
                        setUploadingZoho(true);
                        try {
                          const res = await axios.post(`${API}/admin/upload-to-zoho?target_date=${zohoUploadDate}`);
                          toast.success(`Reports for ${zohoUploadDate} uploaded to Zoho Drive!`);
                          console.log(res.data);
                        } catch (error) {
                          toast.error("Failed to upload to Zoho Drive");
                        } finally {
                          setUploadingZoho(false);
                        }
                      }}
                      disabled={uploadingZoho}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                      data-testid="upload-zoho-btn"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingZoho ? "Uploading..." : "Upload Report"}
                    </Button>
                  </div>
                  <p className="text-vault-text-secondary text-xs">
                    Reports include signature images and check-in/out times.
                  </p>
                </CardContent>
              </Card>

              {/* Database Backup Section */}
              <Card className="bg-vault-surface-highlight/50 border-vault-border max-w-2xl mt-6">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-outfit text-lg font-bold text-vault-gold flex items-center gap-2">
                      <Database className="w-5 h-5" />
                      Database Backup
                    </h3>
                    <span className="text-emerald-400 text-sm flex items-center gap-1">
                      <HardDrive className="w-4 h-4" />
                      Auto-backup at 11:59 PM CT
                    </span>
                  </div>
                  <p className="text-vault-text-secondary text-sm">
                    Create a complete backup of all database records (bookings, guests, employees, rooms) and upload to Zoho Drive.
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      onClick={async () => {
                        setBackingUpDatabase(true);
                        setLastBackupResult(null);
                        try {
                          const res = await axios.post(`${API}/admin/backup-database`);
                          if (res.data.success) {
                            setLastBackupResult(res.data);
                            toast.success("Database backup uploaded to Zoho Drive!");
                          } else {
                            toast.error(res.data.error || "Backup failed");
                          }
                        } catch (error) {
                          toast.error("Failed to create database backup");
                        } finally {
                          setBackingUpDatabase(false);
                        }
                      }}
                      disabled={backingUpDatabase}
                      className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
                      data-testid="backup-database-btn"
                    >
                      <Database className="w-4 h-4" />
                      {backingUpDatabase ? "Backing up..." : "Backup Now"}
                    </Button>
                    
                    {lastBackupResult && (
                      <div className="text-sm text-vault-text-secondary">
                        <span className="text-emerald-400">✓ Backed up:</span>{" "}
                        {Object.entries(lastBackupResult.collections || {}).map(([name, count]) => (
                          <span key={name} className="mr-2">{name}: {count}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-vault-text-secondary text-xs">
                    Includes: bookings, guests, employees, blocked rooms, room configs, and settings.
                  </p>
                </CardContent>
              </Card>

              {/* Admin Security Section */}
              <Card className="bg-vault-surface-highlight/50 border-vault-border max-w-2xl mt-6">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-outfit text-lg font-bold text-vault-gold flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Admin Security
                    </h3>
                  </div>
                  <p className="text-vault-text-secondary text-sm">
                    Change your admin password for security. Password must be at least 8 characters.
                  </p>
                  
                  {!showPasswordChange ? (
                    <Button
                      onClick={() => setShowPasswordChange(true)}
                      className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2"
                      data-testid="show-change-password-btn"
                    >
                      <Key className="w-4 h-4" />
                      Change Password
                    </Button>
                  ) : (
                    <div className="space-y-4 bg-black/30 rounded-lg p-4 border border-vault-border">
                      {/* Step 1: Request OTP */}
                      {!otpSent && (
                        <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-3 mb-4">
                          <p className="text-amber-400 text-sm flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            For security, you'll need to verify via email OTP
                          </p>
                          <Button
                            onClick={handleRequestPasswordChangeOTP}
                            disabled={sendingOtp}
                            className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="request-password-change-otp-btn"
                          >
                            {sendingOtp ? "Sending OTP..." : "Send Verification Code"}
                          </Button>
                        </div>
                      )}
                      
                      {/* OTP Sent - Show form */}
                      {otpSent && (
                        <>
                          <div className="bg-emerald-900/20 border border-emerald-600/30 rounded-lg p-3">
                            <p className="text-emerald-400 text-sm">OTP sent to your email. Enter it below along with your passwords.</p>
                          </div>
                          
                          <div>
                            <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Verification Code (OTP)</label>
                            <Input
                              type="text"
                              value={passwordForm.otp}
                              onChange={(e) => setPasswordForm({...passwordForm, otp: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                              placeholder="Enter 6-digit code"
                              className="bg-vault-surface border-vault-border text-vault-text text-center text-xl tracking-[0.3em] font-mono"
                              maxLength={6}
                              data-testid="password-change-otp-input"
                            />
                          </div>
                        </>
                      )}
                      
                      <div>
                        <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Current Password</label>
                        <Input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                          placeholder="Enter current password"
                          className="bg-vault-surface border-vault-border text-vault-text"
                          data-testid="current-password-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">New Password</label>
                        <Input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                          placeholder="Enter new password (min 8 characters)"
                          className="bg-vault-surface border-vault-border text-vault-text"
                          data-testid="new-password-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Confirm New Password</label>
                        <Input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                          placeholder="Confirm new password"
                          className="bg-vault-surface border-vault-border text-vault-text"
                          data-testid="confirm-password-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleChangePassword}
                          disabled={changingPassword || !otpSent}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="submit-change-password-btn"
                        >
                          {changingPassword ? "Changing..." : "Update Password"}
                        </Button>
                        <Button
                          onClick={() => {
                            setShowPasswordChange(false);
                            setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "", otp: "" });
                            setOtpSent(false);
                          }}
                          variant="outline"
                          className="border-vault-border text-vault-text"
                          data-testid="cancel-change-password-btn"
                        >
                          Cancel
                        </Button>
                        {otpSent && (
                          <Button
                            onClick={handleRequestPasswordChangeOTP}
                            disabled={sendingOtp}
                            variant="outline"
                            className="border-amber-600 text-amber-400"
                            data-testid="resend-password-change-otp-btn"
                          >
                            {sendingOtp ? "Sending..." : "Resend OTP"}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Telegram Settings Card */}
              <Card className="bg-vault-surface-highlight border-vault-border max-w-2xl mt-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-vault-gold flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    Telegram Notification Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Find Groups Button and Info */}
                  <div className="bg-vault-surface rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-vault-text-secondary text-sm">
                        Add your bot to a Telegram group, then click "Find Groups" to auto-detect it.
                      </p>
                      <Button
                        onClick={discoverTelegramGroups}
                        disabled={discoverGroupsLoading}
                        variant="outline"
                        size="sm"
                        className="border-emerald-600 text-emerald-400 hover:bg-emerald-900/30"
                        data-testid="find-telegram-groups-btn"
                      >
                        {discoverGroupsLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Scanning...
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-2" />
                            Find Groups
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* Discovered Groups List */}
                    {discoveredGroups.length > 0 && showGroupSelector && (
                      <div className="border border-emerald-600/30 rounded-lg p-3 bg-emerald-900/10">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-semibold text-emerald-400">Discovered Groups ({discoveredGroups.length})</h5>
                          <Button
                            onClick={() => setShowGroupSelector(false)}
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-vault-text-secondary hover:text-vault-text"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {discoveredGroups.map((group, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-vault-surface-highlight rounded p-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-vault-text truncate">{group.title}</p>
                                <p className="text-xs text-vault-text-secondary font-mono">{group.chat_id}</p>
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button
                                  onClick={() => selectGroupForChannel(group, 'checkin')}
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-7 px-2 hover:bg-blue-900/30 hover:text-blue-400"
                                  title="Use for Check-in"
                                >
                                  <DoorOpen className="w-3 h-3 mr-1" />
                                  In
                                </Button>
                                <Button
                                  onClick={() => selectGroupForChannel(group, 'checkout')}
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-7 px-2 hover:bg-red-900/30 hover:text-red-400"
                                  title="Use for Check-out"
                                >
                                  <LogOut className="w-3 h-3 mr-1" />
                                  Out
                                </Button>
                                <Button
                                  onClick={() => selectGroupForChannel(group, 'reports')}
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-7 px-2 hover:bg-purple-900/30 hover:text-purple-400"
                                  title="Use for Reports"
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  Reports
                                </Button>
                                <Button
                                  onClick={() => selectGroupForChannel(group, 'cleaning')}
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-7 px-2 hover:bg-amber-900/30 hover:text-amber-400"
                                  title="Use for Cleaning"
                                >
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Clean
                                </Button>
                                <Button
                                  onClick={() => selectGroupForChannel(group, 'default')}
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs h-7 px-2 hover:bg-gray-700 hover:text-gray-300"
                                  title="Use as Default"
                                >
                                  <Bell className="w-3 h-3 mr-1" />
                                  Default
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-vault-text-secondary mt-2">
                          Click a button to assign the group to that channel. Don't forget to Save at the bottom!
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Check-In Channel */}
                  <div className="border border-vault-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-vault-gold flex items-center gap-2">
                        <DoorOpen className="w-4 h-4" />
                        Check-In Alerts
                      </h4>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={telegramSettings.checkin_channel?.enabled ?? true}
                          onChange={(e) => updateTelegramChannel('checkin', 'enabled', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-vault-text-secondary">Enabled</span>
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Chat ID (e.g., -1001234567890)"
                        value={telegramSettings.checkin_channel?.chat_id || ''}
                        onChange={(e) => updateTelegramChannel('checkin', 'chat_id', e.target.value)}
                        className="bg-vault-surface border-vault-border text-vault-text"
                        data-testid="checkin-channel-chat-id"
                      />
                      <Button
                        onClick={() => testTelegramChannel('checkin')}
                        disabled={telegramTestingChannel === 'checkin'}
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-400"
                      >
                        {telegramTestingChannel === 'checkin' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                      </Button>
                    </div>
                    <p className="text-xs text-vault-text-secondary">Receives: Guest check-ins, new guest alerts</p>
                  </div>

                  {/* Check-Out Channel */}
                  <div className="border border-vault-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-vault-gold flex items-center gap-2">
                        <LogOut className="w-4 h-4" />
                        Check-Out Alerts
                      </h4>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={telegramSettings.checkout_channel?.enabled ?? true}
                          onChange={(e) => updateTelegramChannel('checkout', 'enabled', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-vault-text-secondary">Enabled</span>
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Chat ID (e.g., -1001234567890)"
                        value={telegramSettings.checkout_channel?.chat_id || ''}
                        onChange={(e) => updateTelegramChannel('checkout', 'chat_id', e.target.value)}
                        className="bg-vault-surface border-vault-border text-vault-text"
                        data-testid="checkout-channel-chat-id"
                      />
                      <Button
                        onClick={() => testTelegramChannel('checkout')}
                        disabled={telegramTestingChannel === 'checkout'}
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400"
                      >
                        {telegramTestingChannel === 'checkout' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                      </Button>
                    </div>
                    <p className="text-xs text-vault-text-secondary">Receives: Guest check-outs only</p>
                  </div>

                  {/* Reports Channel */}
                  <div className="border border-vault-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-vault-gold flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Reports & Status Alerts
                      </h4>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={telegramSettings.reports_channel?.enabled ?? true}
                          onChange={(e) => updateTelegramChannel('reports', 'enabled', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-vault-text-secondary">Enabled</span>
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Chat ID (e.g., -1001234567890)"
                        value={telegramSettings.reports_channel?.chat_id || ''}
                        onChange={(e) => updateTelegramChannel('reports', 'chat_id', e.target.value)}
                        className="bg-vault-surface border-vault-border text-vault-text"
                      />
                      <Button
                        onClick={() => testTelegramChannel('reports')}
                        disabled={telegramTestingChannel === 'reports'}
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-400"
                      >
                        {telegramTestingChannel === 'reports' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                      </Button>
                    </div>
                    <p className="text-xs text-vault-text-secondary">Receives: Morning/evening status, monthly reports</p>
                  </div>

                  {/* Cleaning Channel */}
                  <div className="border border-vault-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-vault-gold flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Cleaning Alerts
                      </h4>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={telegramSettings.cleaning_channel?.enabled ?? true}
                          onChange={(e) => updateTelegramChannel('cleaning', 'enabled', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-vault-text-secondary">Enabled</span>
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Chat ID (e.g., -1001234567890)"
                        value={telegramSettings.cleaning_channel?.chat_id || ''}
                        onChange={(e) => updateTelegramChannel('cleaning', 'chat_id', e.target.value)}
                        className="bg-vault-surface border-vault-border text-vault-text"
                      />
                      <Button
                        onClick={() => testTelegramChannel('cleaning')}
                        disabled={telegramTestingChannel === 'cleaning'}
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-400"
                      >
                        {telegramTestingChannel === 'cleaning' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                      </Button>
                    </div>
                    
                    {/* Quiet Hours for Cleaning */}
                    <div className="mt-3 p-3 bg-vault-surface rounded-lg">
                      <label className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={telegramSettings.cleaning_channel?.quiet_hours_enabled ?? false}
                          onChange={(e) => updateTelegramChannel('cleaning', 'quiet_hours_enabled', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-vault-text">Enable Quiet Hours</span>
                      </label>
                      {telegramSettings.cleaning_channel?.quiet_hours_enabled && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-vault-text-secondary">No alerts from</span>
                          <Input
                            type="time"
                            value={telegramSettings.cleaning_channel?.quiet_hours_start || '22:00'}
                            onChange={(e) => updateTelegramChannel('cleaning', 'quiet_hours_start', e.target.value)}
                            className="w-28 bg-vault-surface-highlight border-vault-border text-vault-text text-sm"
                          />
                          <span className="text-vault-text-secondary">to</span>
                          <Input
                            type="time"
                            value={telegramSettings.cleaning_channel?.quiet_hours_end || '07:00'}
                            onChange={(e) => updateTelegramChannel('cleaning', 'quiet_hours_end', e.target.value)}
                            className="w-28 bg-vault-surface-highlight border-vault-border text-vault-text text-sm"
                          />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-vault-text-secondary">Receives: Room cleaning alerts with "Mark Clean" button</p>
                  </div>

                  {/* Service Requests Channel */}
                  <div className="border border-vault-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-vault-gold flex items-center gap-2">
                        <Bell className="w-4 h-4" />
                        Service Requests Channel
                      </h4>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={telegramSettings.requests_channel?.enabled ?? true}
                          onChange={(e) => updateTelegramChannel('requests', 'enabled', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-vault-text-secondary">Enabled</span>
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Chat ID (e.g., -1001234567890)"
                        value={telegramSettings.requests_channel?.chat_id || ''}
                        onChange={(e) => updateTelegramChannel('requests', 'chat_id', e.target.value)}
                        className="bg-vault-surface border-vault-border text-vault-text"
                      />
                      <Button
                        onClick={() => selectGroupForChannel(null, 'requests')}
                        variant="outline"
                        size="sm"
                        className="border-purple-600 text-purple-400"
                      >
                        Select
                      </Button>
                      <Button
                        onClick={() => testTelegramChannel('requests')}
                        disabled={telegramTestingChannel === 'requests'}
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-400"
                      >
                        {telegramTestingChannel === 'requests' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={telegramSettings.requests_channel?.quiet_hours_enabled ?? false}
                          onChange={(e) => updateTelegramChannel('requests', 'quiet_hours_enabled', e.target.checked)}
                          className="rounded"
                        />
                        Quiet Hours
                      </label>
                      {telegramSettings.requests_channel?.quiet_hours_enabled && (
                        <div className="flex items-center gap-1">
                          <Input
                            type="time"
                            value={telegramSettings.requests_channel?.quiet_hours_start || '22:00'}
                            onChange={(e) => updateTelegramChannel('requests', 'quiet_hours_start', e.target.value)}
                            className="w-28 bg-vault-surface-highlight border-vault-border text-vault-text text-sm"
                          />
                          <span>to</span>
                          <Input
                            type="time"
                            value={telegramSettings.requests_channel?.quiet_hours_end || '07:00'}
                            onChange={(e) => updateTelegramChannel('requests', 'quiet_hours_end', e.target.value)}
                            className="w-28 bg-vault-surface-highlight border-vault-border text-vault-text text-sm"
                          />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-vault-text-secondary">Receives: Towels, pillows, maintenance, and other room service requests</p>
                  </div>

                  {/* Default/Fallback Channel */}
                  <div className="border border-vault-border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-vault-gold flex items-center gap-2">
                        <Bell className="w-4 h-4" />
                        Default Channel (Fallback)
                      </h4>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={telegramSettings.default_channel?.enabled ?? true}
                          onChange={(e) => updateTelegramChannel('default', 'enabled', e.target.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-vault-text-secondary">Enabled</span>
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Chat ID (e.g., -1001234567890)"
                        value={telegramSettings.default_channel?.chat_id || ''}
                        onChange={(e) => updateTelegramChannel('default', 'chat_id', e.target.value)}
                        className="bg-vault-surface border-vault-border text-vault-text"
                      />
                      <Button
                        onClick={() => testTelegramChannel('default')}
                        disabled={telegramTestingChannel === 'default'}
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-400"
                      >
                        {telegramTestingChannel === 'default' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                      </Button>
                    </div>
                    <p className="text-xs text-vault-text-secondary">Receives: All alerts if specific channel not configured</p>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={saveTelegramSettings}
                      disabled={telegramLoading}
                      className="bg-vault-gold hover:bg-amber-600 text-vault-dark"
                    >
                      {telegramLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Telegram Settings
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Info Box */}
              <Card className="bg-vault-surface-highlight/50 border-vault-border max-w-2xl mt-6">
                <CardContent className="p-6">
                  <h3 className="font-outfit text-lg font-bold text-vault-gold mb-3">How AI Verification Works</h3>
                  <ol className="text-vault-text-secondary text-sm space-y-2 list-decimal list-inside">
                    <li>AI agent logs into API Global portal with your credentials</li>
                    <li>Loads sign-in sheets for the previous day</li>
                    <li>Matches names with your Hodler Inn records</li>
                    <li>Auto-fills Employee ID and Room Number for each match</li>
                    <li>Marks "No Bill" for entries not in your records</li>
                    <li>Sends Telegram notification with sync results</li>
                  </ol>
                  <div className="bg-emerald-900/30 border border-emerald-600/30 rounded-lg p-3 mt-4">
                    <p className="text-emerald-400 text-sm">
                      <strong>✓ Auto-Sync Available:</strong> Enable the toggle above to automatically run verification daily at 3 PM
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Bitsy QR Code for Walk-In Guests */}
              <div className="max-w-2xl mt-6">
                <BitsyQRCode hotelName="Hodler Inn" />
              </div>
    </>
  );
}
